import { FC, useState } from 'react'
import { cn } from '@lib/cn'
import { Text } from '@components/rolex-ui/Text'
import { Icon } from '@components/rolex-ui/Icon'
import { Content, Root, Trigger } from '@radix-ui/react-collapsible'

type Props = {
  label: string
  defaultOpen?: boolean
  isLast?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const Accordion: FC<Props> = ({
  label,
  children,
  defaultOpen = false,
  isLast = false,
  open,
  onOpenChange,
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen)

  const isControlledExternally = open !== undefined && onOpenChange
  const isOpen = open ?? uncontrolledIsOpen

  const handleOpenChange = (open: boolean) => {
    if (isControlledExternally) {
      onOpenChange(open)
    } else {
      setUncontrolledIsOpen(open)
    }
  }

  return (
    <>
      <Root open={isOpen} onOpenChange={handleOpenChange}>
        <Trigger asChild>
          <button
            className={cn(
              'font-body flex w-full items-center justify-between border-t border-t-rolexGrey py-5 text-sm',
              'md:h-18 md:text-lg',
              isLast && !isOpen && 'border-b border-b-rolexGrey',
            )}
          >
            <Text variant="body24" className="text-start text-rolexBrown">
              {label}
            </Text>
            <Icon icon={isOpen ? 'minus' : 'plus'} className="h-5 w-5" />
          </button>
        </Trigger>

        <Content
          className={cn(
            'overflow-hidden',
            'data-[state=open]:animate-slide-down',
            'data-[state=closed]:animate-slide-up',
            isLast && isOpen && 'border-b border-b-rolexGrey',
          )}
        >
          <>
            {children}
            <div className="h-8" />
          </>
        </Content>
      </Root>
    </>
  )
}

export default Accordion
